import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, RefreshCw, PlugZap, Instagram, ChevronDown, ChevronRight, ExternalLink, Info } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { adminApi, apiError } from "../api";

const EMPTY = {
  enabled: false,
  handle: "randztoursandtravel1",
  ig_user_id: "",
  access_token: "",
  ttl_seconds: 1800,
  limit: 12,
};

export default function InstagramManager() {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: p }] = await Promise.all([
        adminApi.get("/admin/instagram/settings"),
        adminApi.get("/admin/instagram/preview"),
      ]);
      setMeta(s);
      setForm((f) => ({
        ...f,
        enabled: !!s.enabled,
        handle: s.handle || EMPTY.handle,
        ig_user_id: s.ig_user_id || "",
        ttl_seconds: s.ttl_seconds || 1800,
        limit: s.limit || 12,
        access_token: "", // never prefill token; user can leave blank to keep existing
      }));
      setPreview(p.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.access_token) delete payload.access_token; // keep existing
      payload.ttl_seconds = Number(payload.ttl_seconds) || 1800;
      payload.limit = Number(payload.limit) || 12;
      const { data } = await adminApi.put("/admin/instagram/settings", payload);
      setMeta(data);
      setForm((s) => ({ ...s, access_token: "" }));
      toast.success("Instagram settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const body = {};
      if (form.access_token) body.access_token = form.access_token;
      if (form.ig_user_id) body.ig_user_id = form.ig_user_id;
      const { data } = await adminApi.post("/admin/instagram/test", body);
      toast.success(`Connected to @${data.username} (${data.media_count} posts)`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await adminApi.post("/admin/instagram/refresh");
      toast.success(`Fetched ${data.count} posts from Instagram`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-instagram-page" className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          <Instagram className="h-6 w-6 text-pink-500" /> Instagram Feed
        </h1>
        <p className="text-sm text-muted-foreground">
          Connect your Instagram Business/Creator account to display your latest posts and reels on the homepage.
        </p>
      </div>

      {/* Status card */}
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 grid gap-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Status</div>
            <div className="text-lg font-semibold flex items-center gap-2 mt-1">
              {meta?.enabled && meta?.has_token ? (
                <span className="text-emerald-600">● Live</span>
              ) : meta?.has_token ? (
                <span className="text-amber-600">● Configured — disabled</span>
              ) : (
                <span className="text-slate-500">● Not connected</span>
              )}
              {meta?.cached_count > 0 && (
                <span className="text-xs text-muted-foreground">{meta.cached_count} cached posts</span>
              )}
            </div>
            {meta?.last_synced_at && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Last synced: {new Date(meta.last_synced_at).toLocaleString()}
              </div>
            )}
            {meta?.last_error && (
              <div className="text-xs text-red-500 mt-1">Last error: {meta.last_error}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, enabled: v }))}
              data-testid="ig-enabled-toggle"
            />
            <Label className="text-sm">Show on site</Label>
          </div>
        </div>
      </div>

      {/* Help / instructions */}
      <div className="rounded-2xl border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
          data-testid="ig-help-toggle"
        >
          <span className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
            <Info className="h-4 w-4" /> How to get your Access Token & IG User ID
          </span>
          {showHelp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showHelp && (
          <div className="px-5 pb-5 text-sm text-amber-900 dark:text-amber-200 space-y-3">
            <p><strong>Prerequisites:</strong> Your Instagram account (@{form.handle}) must be a <em>Business</em> or <em>Creator</em> account, and must be linked to a Facebook Page.</p>
            <ol className="list-decimal ml-5 space-y-2">
              <li>
                Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Meta Developers → My Apps <ExternalLink className="h-3 w-3" /></a> and click <b>Create App</b>. Choose type <b>Business</b>.
              </li>
              <li>Inside your app, add the products <b>Facebook Login for Business</b> and <b>Instagram</b> (Graph API).</li>
              <li>
                Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Graph API Explorer <ExternalLink className="h-3 w-3" /></a>. Select your app, then click <b>Generate Access Token</b> and grant these permissions:
                <div className="mt-1 flex flex-wrap gap-1">
                  {["instagram_basic", "pages_show_list", "pages_read_engagement", "business_management"].map((p) => (
                    <code key={p} className="px-1.5 py-0.5 rounded bg-amber-200/60 text-[11px]">{p}</code>
                  ))}
                </div>
              </li>
              <li>Choose the Facebook Page linked to your Instagram, then approve.</li>
              <li>
                In the Explorer, run this query to find your IG User ID:{" "}
                <code className="px-1.5 py-0.5 rounded bg-amber-200/60 text-[11px]">me/accounts?fields=instagram_business_account</code>
                . Copy the <code>instagram_business_account.id</code> — paste it in the field below.
              </li>
              <li>
                Exchange the short-lived token for a <b>long-lived token</b> (~60 days) using{" "}
                <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Access Token Debugger <ExternalLink className="h-3 w-3" /></a>, or call{" "}
                <code className="px-1.5 py-0.5 rounded bg-amber-200/60 text-[11px]">GET /oauth/access_token?grant_type=fb_exchange_token&amp;client_id=APP_ID&amp;client_secret=APP_SECRET&amp;fb_exchange_token=SHORT_TOKEN</code>
              </li>
              <li>Paste the long-lived token below, click <b>Test Connection</b>, then <b>Save</b>, then <b>Refresh feed</b>. That&apos;s it — your homepage will show live posts.</li>
            </ol>
            <p className="text-xs opacity-80">Tokens last ~60 days. You&apos;ll see a &ldquo;Last error&rdquo; here when it&apos;s time to renew.</p>
          </div>
        )}
      </div>

      {/* Config form */}
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 grid gap-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>Instagram handle (without @)</Label>
            <Input value={form.handle} onChange={set("handle")} data-testid="ig-handle" />
          </div>
          <div className="grid gap-2">
            <Label>Instagram User ID</Label>
            <Input value={form.ig_user_id} onChange={set("ig_user_id")} placeholder="17841400000000000" data-testid="ig-user-id" />
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Access Token (long-lived) {meta?.has_token && <span className="text-xs text-muted-foreground font-normal">— Existing: {meta.access_token_masked}. Leave blank to keep.</span>}</Label>
          <Input type="password" value={form.access_token} onChange={set("access_token")} placeholder="EAAG…" data-testid="ig-access-token" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>Cache TTL (seconds)</Label>
            <Input type="number" value={form.ttl_seconds} onChange={set("ttl_seconds")} data-testid="ig-ttl" />
            <p className="text-xs text-muted-foreground">How long to serve cached posts before hitting Instagram again. Default 1800 (30 min).</p>
          </div>
          <div className="grid gap-2">
            <Label>Max posts to fetch</Label>
            <Input type="number" value={form.limit} onChange={set("limit")} data-testid="ig-limit" />
            <p className="text-xs text-muted-foreground">Homepage displays 6. Fetching a few extra lets us fall back if some are removed.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={test} variant="outline" disabled={testing} data-testid="ig-test-btn">
            <PlugZap className="h-4 w-4 mr-2" /> {testing ? "Testing…" : "Test connection"}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="ig-save-btn">
            {saving ? "Saving…" : "Save settings"}
          </Button>
          <Button onClick={refresh} variant="outline" disabled={refreshing || !meta?.has_token} data-testid="ig-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Fetching…" : "Refresh feed now"}
          </Button>
          {meta?.handle && (
            <a href={`https://instagram.com/${meta.handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground px-3 py-2">
              <LinkIcon className="h-3.5 w-3.5" /> @{meta.handle}
            </a>
          )}
        </div>
      </div>

      {/* Preview */}
      {preview.length > 0 && (
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Preview — cached posts</h2>
            <span className="text-xs text-muted-foreground">{preview.length} items</span>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {preview.slice(0, 12).map((p) => (
              <a
                key={p.id}
                href={p.permalink}
                target="_blank"
                rel="noreferrer"
                className="relative aspect-square block rounded-lg overflow-hidden group"
                title={p.caption}
              >
                <img src={p.thumbnail_url || p.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                {p.media_type === "VIDEO" && (
                  <span className="absolute bottom-1 right-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">REEL</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
